import { useTranslations } from '@vocab/react';
import { IconSearch, Autosuggest } from 'braid-design-system';
import React, {
  type FunctionComponent,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
  type ComponentProps,
} from 'react';

import translations from '@seek/rr-caja/src/.vocab';

import { captureException } from '../../errorLogging';
import { useDebounce } from '../../hooks/useDebounce';
import {
  type RecommendedQuestion,
  type SearchResponse,
  search as searchApi,
} from '../../sdk';
import { useConfig } from '../Config/ConfigProvider';
import { useJobDetails } from '../JobDetails/JobDetailsProvider';
import { AnalyticsContext } from '../RoleRequirements/RoleRequirements';

interface Props {
  onSelect: (value: RecommendedQuestion) => void;
  questions: RecommendedQuestion[];
  questionnaireId: string;
}

const RecommendedQuestionAutosuggest = Autosuggest<RecommendedQuestion>;
type AutoSuggestProps = ComponentProps<typeof RecommendedQuestionAutosuggest>;

type Suggestions = { message: string } | AutoSuggestProps['suggestions'];

export const Search: FunctionComponent<Props> = ({
  onSelect,
  questions,
  questionnaireId,
}) => {
  const analytics = useRef(useContext(AnalyticsContext));
  const alreadyShownQuestions = useRef(questions);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search.trim(), 600);
  const { country, locale } = useConfig();
  const jobDetailsCountry = useJobDetails()?.countryId || 'au';
  const { t } = useTranslations(translations);
  const emptyMessage = t('search.noresults');

  const reducer = (
    _: Suggestions,
    searchResult: SearchResponse | undefined,
  ): Suggestions => {
    if (!searchResult?.results) {
      return [];
    }

    const certsyVerifiableQuestions = searchResult.results.filter(
      (r) => r.certsyVerifiable,
    );
    const otherQuestions = searchResult.results.filter(
      (r) => !r.certsyVerifiable,
    );

    return [
      {
        label: 'Questions verified by SEEK Pass',
        suggestions: certsyVerifiableQuestions.map((q) => ({
          text: q.text,
          value: q,
          highlights: highlights(q, search),
        })),
      },
      {
        label: certsyVerifiableQuestions.length > 0 ? 'All results' : '',
        suggestions: otherQuestions.map((q) => ({
          text: q.text,
          value: q,
          highlights: highlights(q, search),
        })),
      },
    ];
  };

  const [suggestions, updateSearchResults] = useReducer(reducer, []);

  useEffect(() => {
    alreadyShownQuestions.current = questions;
  }, [questions]);

  useEffect(() => {
    if (!debouncedSearch) {
      return;
    }

    analytics.current.sendQuestionsSearchedEvent();

    searchApi(
      debouncedSearch,
      jobDetailsCountry,
      alreadyShownQuestions.current,
      locale,
      questionnaireId,
    )
      .then(updateSearchResults)
      .catch((err) => {
        captureException(err);
        updateSearchResults({
          totalCount: 0,
          results: [],
        });
      });
  }, [debouncedSearch, country, jobDetailsCountry, locale, questionnaireId]);

  const clearSearch = () => {
    setSearch('');
    updateSearchResults(undefined);
  };

  const handleChange = (result: AutoSuggestProps['value']) => {
    if (isSuggestionWithValue(result) && result.value) {
      onSelect(result.value);
    } else if (typeof result.text === 'string') {
      setSearch(result.text);
    } else {
      clearSearch();
    }
  };

  return (
    <RecommendedQuestionAutosuggest
      id="search"
      type="search"
      value={{
        text: search || '',
      }}
      suggestions={suggestions}
      noSuggestionsMessage={search ? emptyMessage : ''}
      onClear={clearSearch}
      onChange={handleChange}
      onBlur={() => {
        if (!search) {
          analytics.current.sendSearchInteractedEvent();
        }
      }}
      placeholder={t('search.placeholder')}
      aria-label={t('search.placeholder')}
      icon={<IconSearch />}
    />
  );
};

const highlights = (
  result: RecommendedQuestion,
  searchTerm: string,
): Array<{
  start: number;
  end: number;
}> => {
  const trimmed = searchTerm.trimRight().toLocaleLowerCase();
  const pos = result.text.toLocaleLowerCase().indexOf(trimmed);

  if (pos < 0) {
    return [];
  }

  return [{ start: pos, end: pos + trimmed.length }];
};

function isSuggestionWithValue(
  result: AutoSuggestProps['value'] | { message: string },
): result is AutoSuggestProps['value'] {
  return (result as AutoSuggestProps['value']).value !== undefined;
}
